import { useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useGetClientsGohighlevelImports,
  useGetClientsGohighlevelTags,
  useGetClientsPosImports,
} from '@/api/generated/clients/clients';
import type { ClientOptionsMetadata } from '../components/client-options-types';

/** `?importBatchId=<id>` — "See these people" after an import: the list opens on that import's people. */
export interface ClientImportBatchSearch {
  importBatchId?: string;
}

/** An import batch id (a cuid). Anything else never reaches the API. */
const IMPORT_BATCH_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function validateClientImportBatchSearch(search: Record<string, unknown>): ClientImportBatchSearch {
  const { importBatchId } = search;
  return typeof importBatchId === 'string' && IMPORT_BATCH_ID.test(importBatchId) ? { importBatchId } : {};
}

const LOOKUP_STALE_MS = 5 * 60 * 1000;

/**
 * The salon's GoHighLevel tag counts for the Filters drawer. `undefined` until loaded (or on a failed read):
 * the drawer then shows no GoHighLevel group, the same as for a salon that never imported.
 */
export function useClientsGoHighLevelMetadata(enabled: boolean): ClientOptionsMetadata['goHighLevel'] {
  const tagsQuery = useGetClientsGohighlevelTags({ query: { enabled, staleTime: LOOKUP_STALE_MS } });
  const data = tagsQuery.data?.data;
  return useMemo(
    () => (data ? { importedPeopleCount: data.importedPeopleCount, tags: data.items } : undefined),
    [data]
  );
}

/**
 * The file name behind the "From import <file>" chip — a GoHighLevel or a POS customer import. Import history is
 * admin-equivalent only, like the import itself (the only way to get a batch id); others, or a batch no longer
 * listed, get `null`.
 */
export function useImportBatchFileName(importBatchId: string, canReadImports: boolean): string | null {
  const query = { enabled: Boolean(importBatchId) && canReadImports, staleTime: LOOKUP_STALE_MS };
  const goHighLevelImports = useGetClientsGohighlevelImports({ query });
  const posImports = useGetClientsPosImports({ query });
  if (!importBatchId) return null;
  const batches = [...(goHighLevelImports.data?.data ?? []), ...(posImports.data?.data ?? [])];
  return batches.find((batch) => batch.id === importBatchId)?.fileName ?? null;
}

/**
 * Whether the salon has any GoHighLevel or POS import (the Filters drawer then offers Import history). Read for
 * admin-equivalent viewers only, ahead of the drawer opening so the button never pops in; two small requests that
 * share their cache with the history list and the "From import <file>" chip.
 */
export function useHasClientImports(enabled: boolean): boolean {
  const query = { enabled, staleTime: LOOKUP_STALE_MS };
  const goHighLevelImports = useGetClientsGohighlevelImports({ query });
  const posImports = useGetClientsPosImports({ query });
  return (goHighLevelImports.data?.data.length ?? 0) + (posImports.data?.data.length ?? 0) > 0;
}

/**
 * Hands a `?importBatchId=` from the URL to the page once (it becomes an ordinary, removable filter), then drops
 * it from the URL so the Text view's own `?text=` navigation and a later refresh start from the plain list.
 * `orgRouteParam` is the URL's `$orgId` segment (the salon short code); `onApply` must be stable.
 */
export function useApplyImportBatchSearch(
  orgRouteParam: string,
  importBatchId: string | undefined,
  onApply: (importBatchId: string) => void
) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!importBatchId) return;
    onApply(importBatchId);
    void navigate({
      to: '/$orgId/clients',
      params: { orgId: orgRouteParam },
      search: ({ importBatchId: _importBatchId, ...rest }: ClientImportBatchSearch & Record<string, unknown>) => rest,
      replace: true,
    });
  }, [importBatchId, navigate, onApply, orgRouteParam]);
}
